// src/utils/settingsManager.js - Cloud-based Settings Manager
import { DeviceDetection } from './deviceDetection';

export class SettingsManager {
  static MAX_RECENT_PRESETS = 5;

  /**
   * Upload XML content to Vercel Blob storage and return the URL
   */
  static async uploadXMLToBlob(xmlContent, fileName) {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      // Create a FormData object with the XML content as a file
      const formData = new FormData();
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      formData.append('file', blob, fileName);

      const response = await fetch(`${API_BASE}/api/musicxml?operation=upload`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        return data.url;
      } else {
        throw new Error('Failed to upload XML to blob storage');
      }
    } catch (error) {
      console.error('Upload XML to blob error:', error);
      throw error;
    }
  }

  /**
   * Download XML content from Vercel Blob storage by filename
   */
  static async downloadXMLFromBlob(fileName) {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      // First get the list of files to find the URL for this filename
      const listResponse = await fetch(`${API_BASE}/api/musicxml?operation=list`);
      if (!listResponse.ok) {
        throw new Error(`Failed to list blob storage files: ${listResponse.status}`);
      }
      
      const { files } = await listResponse.json();
      
      const file = files.find(f => f.filename === fileName);
      
      if (!file) {
        console.error(`File "${fileName}" not found. Available files:`, files.map(f => f.filename));
        throw new Error(`XML file not found in blob storage: ${fileName}. Available files: ${files.map(f => f.filename).join(', ')}`);
      }
      
      // Now download the file using its URL
      const response = await fetch(`${API_BASE}/api/musicxml?operation=download&url=${encodeURIComponent(file.url)}`);

      if (response.ok) {
        const content = await response.text();
        return content;
      } else {
        throw new Error(`Failed to download XML from blob storage: ${response.status}`);
      }
    } catch (error) {
      console.error('Download XML from blob error:', error);
      throw error;
    }
  }

  /**
   * Get device-specific preset name for database storage
   */
  static getDeviceSpecificPresetName(name) {
    const deviceCategory = DeviceDetection.getDeviceCategory();
    return `${name}__${deviceCategory}`;
  }

  /**
   * Get display name from device-specific preset name
   */
  static getDisplayNameFromPresetName(deviceSpecificName) {
    const deviceCategory = DeviceDetection.getDeviceCategory();
    const suffix = `__${deviceCategory}`;
    if (deviceSpecificName.endsWith(suffix)) {
      return deviceSpecificName.slice(0, -suffix.length);
    }
    return deviceSpecificName;
  }

  /**
   * Save all application settings to a named preset (cloud only)
   */
  static async savePreset(name, settings, userId) {
    if (!userId) {
      throw new Error('Must be signed in to save presets');
    }

    try {
      const deviceSpecificName = this.getDeviceSpecificPresetName(name);
      
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/settings?operation=savePreset&userId=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          presetName: deviceSpecificName,
          settings: {
            ...settings,
            displayName: name, // Store the original name for display
            savedAt: new Date().toISOString(),
            version: '1.0'
          }
        }),
      });

      if (response.ok) {
        await this.setCurrentPreset(deviceSpecificName, userId);
        return true;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save preset');
      }
    } catch (error) {
      console.error('Save preset error:', error);
      throw error;
    }
  }

  /**
   * Load settings from a named preset (cloud only)
   */
  static async loadPreset(name, userId) {
    if (!userId) {
      throw new Error('Must be signed in to load presets');
    }

    try {
      const deviceSpecificName = this.getDeviceSpecificPresetName(name);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/settings?operation=loadPreset&userId=${userId}&presetName=${encodeURIComponent(deviceSpecificName)}`);
      
      if (response.ok) {
        const data = await response.json();
        await this.setCurrentPreset(deviceSpecificName, userId);
        return data.preset;
      } else if (response.status === 404) {
        return null;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to load preset');
      }
    } catch (error) {
      console.error('Load preset error:', error);
      throw error;
    }
  }

  /**
   * Get all saved presets (cloud only)
   */
  static async getAllPresets(userId) {
    if (!userId) {
      return {};
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/settings?operation=getAllPresets&userId=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        return data.presets || {};
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get presets');
      }
    } catch (error) {
      console.error('Get all presets error:', error);
      return {};
    }
  }

  /**
   * Get presets for the current device type only
   */
  static async getPresetsForCurrentDevice(userId) {
    if (!userId) {
      return {};
    }

    try {
      const allPresets = await this.getAllPresets(userId);
      const currentDeviceCategory = DeviceDetection.getDeviceCategory();
      const suffix = `__${currentDeviceCategory}`;
      const filteredPresets = {};

      // Filter presets that belong to the current device category
      Object.entries(allPresets).forEach(([deviceSpecificName, preset]) => {
        if (deviceSpecificName.endsWith(suffix)) {
          // Use display name if available, otherwise extract from device-specific name
          const displayName = preset.displayName || this.getDisplayNameFromPresetName(deviceSpecificName);
          filteredPresets[displayName] = preset;
        }
      });

      console.log(`Found ${Object.keys(filteredPresets).length} presets for ${currentDeviceCategory} device`);
      return filteredPresets;
    } catch (error) {
      console.error('Get presets for current device error:', error);
      return {};
    }
  }

  /**
   * Delete a preset (cloud only)
   */
  static async deletePreset(name, userId) {
    if (!userId) {
      throw new Error('Must be signed in to delete presets');
    }

    try {
      const deviceSpecificName = this.getDeviceSpecificPresetName(name);
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/settings?operation=deletePreset&userId=${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ presetName: deviceSpecificName }),
      });

      if (response.ok) {
        // If this was the current preset, clear it
        const currentPreset = await this.getCurrentPresetName(userId);
        if (currentPreset === deviceSpecificName) {
          await this.setCurrentPreset(null, userId);
        }
        return true;
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete preset');
      }
    } catch (error) {
      console.error('Delete preset error:', error);
      throw error;
    }
  }

  /**
   * Get the currently active preset name (cloud only)
   */
  static async getCurrentPresetName(userId) {
    if (!userId) {
      return null;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/settings?operation=getCurrentPreset&userId=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        const deviceSpecificName = data.currentPreset;
        if (deviceSpecificName) {
          // Return the display name instead of device-specific name
          return this.getDisplayNameFromPresetName(deviceSpecificName);
        }
        return null;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Get current preset error:', error);
      return null;
    }
  }

  /**
   * Set the currently active preset (cloud only)
   */
  static async setCurrentPreset(name, userId) {
    if (!userId) {
      return false;
    }

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/settings?operation=setCurrentPreset&userId=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ presetName: name }),
      });

      return response.ok;
    } catch (error) {
      console.error('Set current preset error:', error);
      return false;
    }
  }

  /**
   * Export all presets as JSON for backup (cloud only)
   */
  static async exportPresets(userId) {
    if (!userId) {
      throw new Error('Must be signed in to export presets');
    }

    const presets = await this.getAllPresets(userId);
    return JSON.stringify(presets, null, 2);
  }

  /**
   * Import presets from JSON (cloud only)
   */
  static async importPresets(jsonString, userId, merge = true) {
    if (!userId) {
      throw new Error('Must be signed in to import presets');
    }

    try {
      const importedPresets = JSON.parse(jsonString);
      
      if (merge) {
        const existingPresets = await this.getAllPresets(userId);
        const mergedPresets = { ...existingPresets, ...importedPresets };
        
        // Save each preset individually
        for (const [name, settings] of Object.entries(mergedPresets)) {
          await this.savePreset(name, settings, userId);
        }
      } else {
        // Replace all presets
        // First, delete all existing presets
        const existingPresets = await this.getAllPresets(userId);
        for (const name of Object.keys(existingPresets)) {
          await this.deletePreset(name, userId);
        }
        
        // Then save imported presets
        for (const [name, settings] of Object.entries(importedPresets)) {
          await this.savePreset(name, settings, userId);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Import presets error:', error);
      throw error;
    }
  }

  /**
   * Collect all current application settings with device-specific handling
   */
  static collectCurrentSettings(playerState, fileContent = null, existingSettings = null, options = null) {
    // Only get minimal device info to avoid triggering autosave on window resize/interaction
    const deviceCategory = DeviceDetection.getDeviceCategory();
    const deviceInfo = {
      category: deviceCategory,
      // Only include stable device info, not dynamic window dimensions
      isMobile: DeviceDetection.isMobile(),
      deviceType: DeviceDetection.getDeviceType()
    };
    
    
    // Collect all current settings (no need for device-specific structure anymore)
    const settings = {
      // Playback settings
      playbackSpeed: playerState.playbackSpeed,
      volume: playerState.volume,
      instrument: playerState.instrument,
      audioLatency: playerState.audioLatency,
      
      // Part settings - reuse existing objects when values haven't changed
      partControls: options?.preservePartControls && existingSettings?.partControls 
        ? existingSettings.partControls  // Use existing partControls to avoid Safari race condition
        : playerState.partControls.map((part, index) => {
            const newPartData = {
              id: part.id,
              name: part.name,
              checked: part.checked,
              visible: part.visible,
              upperVoice: part.upperVoice,
              lowerVoice: part.lowerVoice
            };
            
            // If we have existing settings, check if this part's data is identical
            const existingPart = existingSettings?.partControls?.[index];
            if (existingPart && 
                existingPart.id === newPartData.id &&
                existingPart.name === newPartData.name &&
                existingPart.checked === newPartData.checked &&
                existingPart.visible === newPartData.visible &&
                existingPart.upperVoice === newPartData.upperVoice &&
                existingPart.lowerVoice === newPartData.lowerVoice) {
              // Values are identical, reuse the existing object to maintain reference equality
              return existingPart;
            }
            
            // Values have changed, return new object
            return newPartData;
          }),
      partVolumes: playerState.partVolumes,
      
      // Display settings
      zoom: Math.round(playerState.zoom * 1000) / 1000,
      autoScroll: playerState.autoScroll,
      lookaheadBeats: playerState.lookaheadBeats || 0,
      
      // Confidence mode settings
      selectedPart: playerState.selectedPart,
      confidence: playerState.confidence,
      
      // Swing mode settings
      isSwingMode: playerState.isSwingMode,
      swingRatio: playerState.swingRatio,
      swingRatioSource: playerState.swingRatioSource,

      // Key transposition in half steps
      transposeSteps: playerState.transposeSteps || 0,

      // File info reference
      fileName: playerState.fileName || null
    };
    
    return settings;
  }

  /**
   * Apply loaded settings to the application
   */
  static applySettings(settings, playerActions) {
    try {
      const currentDeviceCategory = DeviceDetection.getDeviceCategory();
      
      // Apply playback settings
      if (settings.playbackSpeed !== undefined) {
        playerActions.setPlaybackSpeed(settings.playbackSpeed);
      }
      
      if (settings.volume !== undefined) {
        playerActions.setVolume(settings.volume);
      }
      
      if (settings.instrument !== undefined) {
        playerActions.setInstrument(settings.instrument);
      }
      
      if (settings.audioLatency !== undefined) {
        playerActions.setAudioLatency(settings.audioLatency);
      }

      // Apply key transposition (notation + audio)
      if (settings.transposeSteps !== undefined && playerActions.setTransposeSteps) {
        playerActions.setTransposeSteps(settings.transposeSteps);
        // Retry after the sheet finishes loading on slower devices; the setter
        // is idempotent so this is a no-op when already applied
        setTimeout(() => {
          playerActions.setTransposeSteps(settings.transposeSteps);
        }, 500);
      }
      
      // Apply part settings with retry mechanism
      if (settings.partControls && Array.isArray(settings.partControls)) {
        const applyPartSettings = () => {
          let appliedCount = 0;
          settings.partControls.forEach(savedPart => {
            // Apply part visibility and muting
            if (playerActions.handlePartToggle) {
              try {
                playerActions.handlePartToggle(savedPart.id, savedPart.checked);
                appliedCount++;
              } catch (e) {
                // Part might not exist yet
              }
            }
            if (playerActions.handlePartVisibilityToggle) {
              try {
                playerActions.handlePartVisibilityToggle(savedPart.id, savedPart.visible);
              } catch (e) {
                // Part might not exist yet
              }
            }
            
            // Apply divisi voice settings
            if (playerActions.handleUpperVoiceToggle && savedPart.upperVoice !== undefined) {
              try {
                playerActions.handleUpperVoiceToggle(savedPart.id, savedPart.upperVoice);
              } catch (e) {
                // Part might not exist yet
              }
            }
            if (playerActions.handleLowerVoiceToggle && savedPart.lowerVoice !== undefined) {
              try {
                playerActions.handleLowerVoiceToggle(savedPart.id, savedPart.lowerVoice);
              } catch (e) {
                // Part might not exist yet
              }
            }
          });
          return appliedCount;
        };
        
        // Try immediate application
        let applied = applyPartSettings();
        
        // If not all parts were applied, retry with progressively longer delays
        if (applied < settings.partControls.length) {
          let retryCount = 0;
          const maxRetries = 5;
          
          const retryApplication = () => {
            retryCount++;
            const newApplied = applyPartSettings();
            
            if (newApplied < settings.partControls.length && retryCount < maxRetries) {
              // Exponential backoff: 50ms, 100ms, 200ms, 400ms, 800ms
              setTimeout(retryApplication, 50 * Math.pow(2, retryCount - 1));
            }
          };
          
          setTimeout(retryApplication, 50);
        }
      }
      
      if (settings.partVolumes) {
        const applyVolumeSettings = () => {
          Object.entries(settings.partVolumes).forEach(([partId, volume]) => {
            if (playerActions.setPartVolume) {
              try {
                playerActions.setPartVolume(partId, volume);
              } catch (e) {
                // Part might not exist yet
              }
            }
          });
        };
        
        // Try immediate application
        applyVolumeSettings();
        
        // Retry after a delay to ensure parts are ready
        setTimeout(() => {
          applyVolumeSettings();
        }, 100);
      }
      
      // Apply display settings
      if (settings.zoom !== undefined) {
        // For mobile Safari during settings load, use direct OSMD manipulation to bypass coordinator issues
        // BUT still update React state to ensure auto-save works
        if (currentDeviceCategory === 'mobile' && playerActions.osmdRef?.current) {
          // CRITICAL: Update React state first to ensure auto-save detects changes
          playerActions.setZoom(settings.zoom);
          
          // Apply direct OSMD zoom with proper null checks
          const osmd = playerActions.osmdRef.current;
          if (osmd && typeof osmd.zoom !== 'undefined') {
            osmd.zoom = settings.zoom;
            
            // Simple render without the complex mobile Safari workarounds that were causing crashes
            if (osmd.render && typeof osmd.render === 'function') {
              try {
                const renderResult = osmd.render();
                // Check if render() returns a promise
                if (renderResult && typeof renderResult.then === 'function') {
                  renderResult.catch(error => {
                    // Silently handle render errors
                  });
                }
              } catch (error) {
                // Silently handle render errors
              }
            }
          }
        } else {
          // Normal path for desktop or non-settings loads
          playerActions.setZoom(settings.zoom);
          
          // Add a small delay for mobile to ensure OSMD is ready
          if (currentDeviceCategory === 'mobile') {
            setTimeout(() => {
              playerActions.setZoom(settings.zoom);
            }, 100);
          }
        }
      }
      
      if (settings.autoScroll !== undefined) {
        playerActions.setAutoScroll(settings.autoScroll);
      }
      
      if (settings.lookaheadBeats !== undefined) {
        playerActions.setLookaheadBeats(settings.lookaheadBeats);
      }
      
      // Restore confidence mode settings
      if (settings.selectedPart !== undefined && playerActions.setSelectedPart) {
        playerActions.setSelectedPart(settings.selectedPart);
      }
      
      if (settings.confidence !== undefined && playerActions.setConfidence) {
        playerActions.setConfidence(settings.confidence);
      }
      
      // Restore swing mode settings
      if (settings.isSwingMode !== undefined && playerActions.setIsSwingMode) {
        playerActions.setIsSwingMode(settings.isSwingMode);
      }
      if (settings.swingRatio !== undefined && playerActions.setSwingRatio) {
        playerActions.setSwingRatio(settings.swingRatio);
      }
      if (settings.swingRatioSource !== undefined && playerActions.setSwingRatioSource) {
        playerActions.setSwingRatioSource(settings.swingRatioSource);
      }

      // Apply confidence to override volumes if both selectedPart and confidence are set
      if (settings.selectedPart !== undefined && settings.confidence !== undefined && 
          settings.partControls && Array.isArray(settings.partControls) &&
          playerActions.applyConfidenceToOverrides) {
        setTimeout(() => {
          const partIds = settings.partControls.map(p => p.id);
          playerActions.applyConfidenceToOverrides(partIds, settings.selectedPart, settings.confidence);
        }, 100); // Small delay to ensure parts are loaded
      }
      
      // Force OSMD re-render to apply visibility changes
      if (playerActions.osmdRef?.current && settings.partControls) {
        setTimeout(() => {
          try {
            // Force OSMD to update instrument visibility based on current settings
            if (playerActions.osmdRef.current.Sheet?.Instruments) {
              settings.partControls.forEach(savedPart => {
                const instrument = playerActions.osmdRef.current.Sheet.Instruments.find(inst => inst.Id === savedPart.id);
                if (instrument) {
                  instrument.Visible = savedPart.visible !== false;
                }
              });
              
              // Force a re-render to apply the visibility changes
              playerActions.osmdRef.current.render();
            }
          } catch (error) {
            console.error('Error forcing OSMD visibility update:', error);
          }
        }, 100);
      }
      
      return true;
    } catch (error) {
      console.error('Apply settings error:', error);
      return false;
    }
  }

  /**
   * Get the list of recently used presets for current device (based on update timestamps)
   */
  static async getRecentPresets(userId) {
    if (!userId) {
      return [];
    }

    try {
      const presets = await this.getPresetsForCurrentDevice(userId);
      const presetEntries = Object.entries(presets);
      
      // Sort by savedAt timestamp, most recent first
      presetEntries.sort((a, b) => {
        const dateA = new Date(a[1].savedAt || 0);
        const dateB = new Date(b[1].savedAt || 0);
        return dateB - dateA;
      });
      
      // Return just the names, limited to max count
      return presetEntries
        .slice(0, this.MAX_RECENT_PRESETS)
        .map(([name]) => name);
    } catch (error) {
      console.error('Get recent presets error:', error);
      return [];
    }
  }

  /**
   * Check if user can save settings
   */
  static canSaveSettings(user) {
    // Must be signed in and have save-settings permission
    return user && user.permissions && user.permissions.includes('save-settings');
  }

  /**
   * Get user-friendly error message for non-signed-in users
   */
  static getSignInMessage() {
    return 'You must be signed in to save and load presets. Sign in to sync your settings across all your devices!';
  }


}